/**
 * Illustration tints — the closed set of accent colours a character
 * illustration can be recoloured to.
 *
 * Deliberately a set of token names rather than free-form colour strings: a
 * call site picks a hue by name, the palette stays the design system's to
 * change (ADR-012), and no caller-supplied string ever reaches the SVG markup.
 * Names describe the hue rather than any feature that wants it, and match
 * SkillGlyph's palette for every token the two share, so one vocabulary spans
 * both closed sets. A drift guard in tint.test.ts holds that agreement — the
 * claim is checked, not asserted in prose.
 *
 * A hue that is not yet a colour token is added to elements/colors.ts first,
 * then named here — never inlined as a hex.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { colors } from '../colors';

export const illustrationTintNames = [
  'sand',
  'periwinkle',
  'paleBlue',
  'moss',
  'palePink',
  'cream',
] as const;

export type IllustrationTint = (typeof illustrationTintNames)[number];

export const ILLUSTRATION_TINTS = {
  sand: colors.background.brand,
  periwinkle: colors.misc.variationJ,
  paleBlue: colors.cerulean['200'],
  moss: colors.mossGreen['300'],
  palePink: colors.punchPink['200'],
  cream: colors.coral['100'],
} as const satisfies Record<IllustrationTint, string>;

export const illustrationTintNameSet: ReadonlySet<string> = new Set(illustrationTintNames);

export function isIllustrationTint(value: string): value is IllustrationTint {
  return illustrationTintNameSet.has(value);
}

/**
 * Colour value for a tint name.
 *
 * Validates rather than returning `undefined` for an unknown name: the schema
 * rejects one at the boundary, but an untyped call site (plain JS, or a GenUI
 * document that bypassed validation) would otherwise substitute the string
 * "undefined" into the markup and render a colourless illustration.
 */
export function illustrationTintValue(tint: IllustrationTint): string {
  const value = ILLUSTRATION_TINTS[tint];
  if (value === undefined) {
    throw new Error(
      `Unknown illustration tint "${tint}". Expected one of: ${illustrationTintNames.join(', ')}.`,
    );
  }
  return value;
}
